// Shared register-business route helpers keep fresh-entry behavior consistent across entry surfaces.

export const REGISTER_BUSINESS_PATH = '/register-business';
export const REGISTER_BUSINESS_PRICING_PATH = '/pricing';
export const REGISTER_BUSINESS_FLOW_QUERY_KEY = 'flow';
export const REGISTER_BUSINESS_FLOW_QUERY_VALUE = 'register-business';

export const REGISTER_BUSINESS_FRESH_ENTRY_STATE = {
  entry: 'fresh',
} as const;

// Mark the pricing page as a registration detour so it can render a return-aware chooser.
export const REGISTER_BUSINESS_PRICING_SELECTION_STATE = {
  entry: 'pricing_selection',
} as const;

// Mark the register-business return path so the selected plan can skip straight to profile.
export const REGISTER_BUSINESS_PRICING_RETURN_STATE = {
  entry: 'pricing_return',
} as const;

export type RegisterBusinessEntryState = typeof REGISTER_BUSINESS_FRESH_ENTRY_STATE;
export type RegisterBusinessPricingSelectionState = typeof REGISTER_BUSINESS_PRICING_SELECTION_STATE;
export type RegisterBusinessPricingReturnState = typeof REGISTER_BUSINESS_PRICING_RETURN_STATE;

// Build a register-business path without duplicating query-string rules across callers.
export function buildRegisterBusinessPath(
  planId?: string | null,
  input?: {
    flow?: boolean;
  },
): string {
  const searchParams = new URLSearchParams();

  if (planId) {
    searchParams.set('plan', planId);
  }

  if (input?.flow) {
    searchParams.set(REGISTER_BUSINESS_FLOW_QUERY_KEY, REGISTER_BUSINESS_FLOW_QUERY_VALUE);
  }

  const query = searchParams.toString();
  return query ? `${REGISTER_BUSINESS_PATH}?${query}` : REGISTER_BUSINESS_PATH;
}

// Build the canonical pricing page URL used by registration compare-and-return flows.
export function buildRegisterBusinessPricingPath(): string {
  return `${REGISTER_BUSINESS_PRICING_PATH}?${REGISTER_BUSINESS_FLOW_QUERY_KEY}=${REGISTER_BUSINESS_FLOW_QUERY_VALUE}`;
}

// Detect whether the current visit came from an explicit "start business registration" action.
export function hasFreshRegisterBusinessEntry(state: unknown): state is RegisterBusinessEntryState {
  if (!state || typeof state !== 'object') {
    return false;
  }

  return (state as { entry?: string }).entry === REGISTER_BUSINESS_FRESH_ENTRY_STATE.entry;
}

// Detect when Pricing was opened from registration so it can offer a return-aware chooser.
export function hasRegisterBusinessPricingSelectionEntry(
  state: unknown,
): state is RegisterBusinessPricingSelectionState {
  if (!state || typeof state !== 'object') {
    return false;
  }

  return (state as { entry?: string }).entry === REGISTER_BUSINESS_PRICING_SELECTION_STATE.entry;
}

// Detect when the user is returning from Pricing after choosing a plan.
export function hasRegisterBusinessPricingReturnEntry(
  state: unknown,
): state is RegisterBusinessPricingReturnState {
  if (!state || typeof state !== 'object') {
    return false;
  }

  return (state as { entry?: string }).entry === REGISTER_BUSINESS_PRICING_RETURN_STATE.entry;
}

// Detect the durable registration flow marker that survives hard refreshes in the URL.
export function hasRegisterBusinessFlowQuery(searchParams: URLSearchParams): boolean {
  return searchParams.get(REGISTER_BUSINESS_FLOW_QUERY_KEY) === REGISTER_BUSINESS_FLOW_QUERY_VALUE;
}
